import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Search,
  ShieldOutlined,
  WhatsApp,
  PhoneOutlined,
  SmsOutlined,
  DeleteOutline,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import WhatsAppButtonSettings from "../auths/WhatsAppButtonSettings";
import { useQueryClient } from "react-query";

const channels = [
  {
    value: "waba",
    label: "WABA",
    description: "WhatsApp Business",
    color: "#059669",
    icon: WhatsApp,
  },
  {
    value: "voice",
    label: "Voice",
    description: "Voice calls",
    color: "#6366f1",
    icon: PhoneOutlined,
  },
  {
    value: "rcs",
    label: "RCS",
    description: "Rich communication services",
    color: "#0284c7",
    icon: SmsOutlined,
  },
];
const endpoint = `${API_ENDPOINT}/api/unsubscribe-settings`;
const errorMessage = (error) =>
  error.response?.data?.message ||
  "Unable to update unsubscribe settings. Please try again.";

const UnsubscribeSettings = () => {
  const queryClient = useQueryClient();
  const [buttonSettingsOpen, setButtonSettingsOpen] = useState(false);
  const [category, setCategory] = useState("waba");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [adding, setAdding] = useState(false);
  const [phones, setPhones] = useState("");
  const [removing, setRemoving] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const channel = channels.find((item) => item.value === category);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    axios
      .get(endpoint, {
        ...getHeader(),
        signal: controller.signal,
        params: { category, search: query, page: page + 1, limit: 25 },
      })
      .then(({ data: result }) => {
        if (page > 0 && result.items.length === 0) {
          setPage(page - 1);
          return;
        }
        setData(result);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setError(errorMessage(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [category, query, page, revision]);

  const invalidateWabaList = () =>
    queryClient.invalidateQueries([
      `${API_ENDPOINT}/api/get-opted-out-numbers`,
    ]);

  const addNumbers = async () => {
    const numbers = phones
      .split(/[\n,;]+/)
      .map((phone) => phone.trim())
      .filter(Boolean);
    if (!numbers.length || numbers.length > 500) {
      setFormError("Enter between 1 and 500 phone numbers.");
      return;
    }
    if (
      numbers.some(
        (phone) =>
          !/^\+?[\d\s().-]+$/.test(phone) ||
          !/^[1-9]\d{6,14}$/.test(phone.replace(/\D/g, ""))
      )
    ) {
      setFormError(
        "Use 7–15 digits including country code, for example +919876543210."
      );
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const { data: result } = await axios.post(
        endpoint,
        { phones: numbers, category },
        getHeader()
      );
      toast.success(
        `${result.added} new${
          result.updated ? ` · ${result.updated} updated` : ""
        }${
          result.duplicates
            ? ` · ${result.duplicates} already listed or repeated`
            : ""
        }`
      );
      invalidateWabaList();
      setAdding(false);
      setPhones("");
      setRevision((value) => value + 1);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const removeNumber = async () => {
    setSaving(true);
    setFormError("");
    try {
      await axios.delete(`${endpoint}/${removing._id}`, getHeader());
      toast.success("Number removed from the unsubscribe list.");
      invalidateWabaList();
      setRemoving(null);
      setRevision((value) => value + 1);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ color: "#6366f1", mb: 1 }}
      >
        <ShieldOutlined fontSize="small" />
        <Typography variant="overline" fontWeight={700}>
          Contacts Hub
        </Typography>
      </Stack>
      <Typography
        variant="h4"
        fontWeight={800}
        sx={{ color: "#0f172a", letterSpacing: "-0.8px" }}
      >
        Unsubscribe settings
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Manage phone numbers opted out of each channel. Each channel has its own
        unsubscribe list.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {channels.map(({ value, label, description, color, icon: Icon }) => (
          <Card
            key={value}
            component="button"
            onClick={() => {
              setCategory(value);
              setPage(0);
            }}
            aria-pressed={category === value}
            sx={{
              textAlign: "left",
              p: 2.5,
              cursor: "pointer",
              borderRadius: 3,
              boxShadow: "none",
              font: "inherit",
              border: "2px solid",
              borderColor: category === value ? color : "#e2e8f0",
              bgcolor: category === value ? `${color}08` : "white",
              "&:hover": { borderColor: color },
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: `${color}12`,
                  color,
                  display: "flex",
                }}
              >
                <Icon />
              </Box>
              <Typography variant="h5" fontWeight={800}>
                {data.counts[value] ?? "—"}
              </Typography>
            </Stack>
            <Typography fontWeight={700} sx={{ mt: 2 }}>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Card>
        ))}
      </Box>
      {category === "waba" && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" onClick={() => setButtonSettingsOpen(true)}>
              Configure buttons
            </Button>
          }
        >
          WABA includes manual entries and opt-outs from your saved button
          triggers. Matching replies sync automatically.
        </Alert>
      )}
      <Card
        variant="outlined"
        sx={{ borderRadius: 3, borderColor: "#e2e8f0", overflow: "hidden" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: 3 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {channel.label} unsubscribe list
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add or remove numbers for {channel.description.toLowerCase()}.
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={() => {
              setFormError("");
              setAdding(true);
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              bgcolor: channel.color,
            }}
          >
            Add numbers
          </Button>
        </Stack>
        <Box sx={{ px: 3, pb: 2 }}>
          <TextField
            size="small"
            placeholder="Search phone numbers"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ "aria-label": "Search phone numbers" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 340 } }}
          />
        </Box>
        {error ? (
          <Alert
            severity="error"
            sx={{ m: 3 }}
            action={
              <Button
                color="inherit"
                onClick={() => setRevision((value) => value + 1)}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : loading ? (
          <Box sx={{ p: 7, textAlign: "center" }} role="status">
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Loading numbers…
            </Typography>
          </Box>
        ) : data.items.length === 0 ? (
          <Box sx={{ py: 7, px: 3, textAlign: "center", bgcolor: "#f8fafc" }}>
            <ShieldOutlined
              sx={{ fontSize: 42, color: channel.color, mb: 1 }}
            />
            <Typography fontWeight={700}>
              {query
                ? "No matching numbers"
                : `No ${channel.label} unsubscribes yet`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {query
                ? "Try a different phone number."
                : "Add phone numbers to start managing this channel’s opt-outs."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell>Phone number</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Added on</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>
                      +{item.phone}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={channel.label}
                        sx={{
                          color: channel.color,
                          bgcolor: `${channel.color}10`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {(item.sources?.length ? item.sources : ["manual"])
                        .map((source) =>
                          source === "waba_button" ? "Button reply" : "Manual"
                        )
                        .join(" + ")}
                    </TableCell>
                    <TableCell>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title="Remove phone number"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Remove ${item.phone}`}
                          onClick={() => {
                            setFormError("");
                            setRemoving(item);
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(211, 47, 47, 0.08)", // Subtle red circle on hover
                            },
                          }}
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!error && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Card>
      <Dialog
        open={buttonSettingsOpen}
        onClose={() => setButtonSettingsOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>WABA opt-out buttons</DialogTitle>
        <DialogContent dividers>
          <WhatsAppButtonSettings
            embedded
            onSaved={() => setRevision((value) => value + 1)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setButtonSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={adding}
        onClose={() => {
          if (!saving) setAdding(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add numbers to {channel.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Include the country code. Paste one number per line, or separate
            with commas. Up to 500 numbers; duplicates are skipped.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={6}
            fullWidth
            label="Phone numbers"
            placeholder={"+919876543210\n+919876543211"}
            value={phones}
            onChange={(event) => setPhones(event.target.value)}
            disabled={saving}
          />
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button disabled={saving} onClick={() => setAdding(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !phones.trim()}
            onClick={addNumbers}
          >
            {saving ? "Saving…" : "Add to unsubscribe list"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(removing)}
        onClose={() => {
          if (!saving) setRemoving(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remove unsubscribe?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove +{removing?.phone} from the {channel.label} unsubscribe list?
            Other channel lists stay unchanged.{" "}
            {category === "waba" &&
              "Historical sync will not add this number back. A new opt-out reply will unsubscribe it again."}
          </Typography>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={saving} onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={removeNumber}
          >
            {saving ? "Removing…" : "Remove number"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UnsubscribeSettings;
